import { useState, useEffect } from "react";
import {
  ContentLayout, Header, Container, Box, Button, Alert, Spinner,
  SpaceBetween, ColumnLayout, ExpandableSection,
} from "@cloudscape-design/components";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from "recharts";
import { boxerService } from "../../services/boxerService";
import MarkdownRenderer from "../../components/common/MarkdownRenderer";
import DataTable from "../../components/common/DataTable";

// ── Reusable section wrapper ─────────────────────────────────────
function Section({ loading, error, children }) {
  if (loading) return (
    <Box textAlign="center" padding="l" color="text-body-secondary">
      <Spinner size="normal" /> &nbsp; Loading...
    </Box>
  );
  if (error) return <Alert type="error">{error}</Alert>;
  return children;
}

// ── SectionBlock ─────────────────────────────────────────────────
function SectionBlock({ table, aiInsight, emptyMsg, columnHighlights, title }) {
  const columns = table && table.length > 0 ? Object.keys(table[0]) : [];
  return (
    <SpaceBetween size="m">
      {aiInsight && aiInsight.trim() && (
        <div style={sStyle.insight}>
          <Box fontWeight="bold" padding={{ bottom: "xs" }}>🔍 AI Insight</Box>
          <MarkdownRenderer text={aiInsight.trim()} />
        </div>
      )}
      {table && table.length > 0
        ? <DataTable columns={columns} data={table} columnHighlights={columnHighlights || {}} title={title} rowCount={table.length}/>
        : <Box color="text-body-secondary" textAlign="center" padding="l">{emptyMsg}</Box>
      }
    </SpaceBetween>
  );
}

const sStyle = {
  insight: { background: "#f8f9fb", border: "1px solid #d5dbdb", borderRadius: 8, padding: "14px 18px" },
};

const initSection = () => ({ data: null, loading: true, error: "" });

const getDefaultDates = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthEnd = new Date(firstOfMonth - 1);
  const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
  return {
    from: lastMonthStart.toISOString().slice(0, 10),
    to:   lastMonthEnd.toISOString().slice(0, 10),
  };
};

export default function DeliveryMonitoring() {
  const defaults = getDefaultDates();
  const [fromDate,      setFromDate]      = useState(defaults.from);
  const [toDate,        setToDate]        = useState(defaults.to);
  const [appliedRange,  setAppliedRange]  = useState(null);

  const [kpisSection,    setKpisSection]    = useState(initSection());
  const [networkSection, setNetworkSection] = useState(initSection());
  const [dcSection,      setDcSection]      = useState(initSection());
  const [storeSection,   setStoreSection]   = useState(initSection());
  const [laneSection,    setLaneSection]    = useState(initSection());

  const fetchSection = async (fetcher, setter) => {
    setter(s => ({ ...s, loading: true, error: "" }));
    try {
      const data = await fetcher();
      setter({ data, loading: false, error: "" });
    } catch (err) {
      setter({ data: null, loading: false, error: err.response?.data?.detail || "Failed to load." });
    }
  };

  const loadAll = (fd = fromDate, td = toDate) => {
    setAppliedRange({ from: fd, to: td });
    // Fire all requests in parallel — each section renders independently
    fetchSection(() => boxerService.getMonKpis(fd, td),            setKpisSection);
    fetchSection(() => boxerService.getMonNetworkWide(fd, td),     setNetworkSection);
    fetchSection(() => boxerService.getMonDcPerformance(fd, td),   setDcSection);
    fetchSection(() => boxerService.getMonStorePerformance(fd, td), setStoreSection);
    fetchSection(() => boxerService.getMonCriticalLane(fd, td),    setLaneSection);
  };

  useEffect(() => { loadAll(defaults.from, defaults.to); }, []);

  const handleApplyFilter = () => loadAll(fromDate, toDate);

  const handleClearFilter = () => {
    const d = getDefaultDates();
    setFromDate(d.from);
    setToDate(d.to);
    loadAll(d.from, d.to);
  };

  // Derived from kpis section
  const kpis   = kpisSection.data?.kpis   || [];
  const target = kpisSection.data?.target || 95;

  const withData    = kpis.filter(k => k.total_orders > 0);
  const totalOrders = withData.reduce((s, k) => s + (Number(k.total_orders) || 0), 0);
  const onTime      = withData.reduce((s, k) => s + (Number(k.orders_shipped_on_time) || 0), 0);
  const late        = withData.reduce((s, k) => s + (Number(k.late_orders) || 0), 0);
  const avgPct      = totalOrders > 0 ? ((onTime / totalOrders) * 100).toFixed(1) : "—";
  const statusColor = avgPct === "—" ? "#666" : parseFloat(avgPct) >= target ? "#1d8102" : parseFloat(avgPct) > 92 ? "#df6a00" : "#d13212";

  const chartMap = {};
  kpis.forEach(k => {
    const d = k.earliest_ship_date;
    if (!d) return;
    if (!chartMap[d]) chartMap[d] = { date: d, total: 0, count: 0 };
    chartMap[d].total += Number(k.shipment_pct) || 0;
    chartMap[d].count += 1;
  });
  const chartData = Object.values(chartMap)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(d => ({ date: d.date, "On-Time %": parseFloat((d.total / d.count).toFixed(1)) }));

  const dcHighlights = {
    "On-Time Orders":        "#0eb922",
    "Orders Late 1 Day":     "#4688df",
    "Orders Late 2 Days":    "#8F8D0B",
    "Orders Late (≥3 Days)": "#ff4c46",
    "Unshipped Orders":      "#f82828",
  };

  const kpiColumns = kpis.length > 0 ? Object.keys(kpis[0]) : [];

  return (
    <ContentLayout
      header={
        <Header variant="h1"
          description="Monitor and improve delivery performance across distribution network."
          actions={<Button iconName="refresh" onClick={() => loadAll(fromDate, toDate)}>Refresh Data</Button>}
        >
          🎛️ InsightIQ — Delivery Adherence Monitoring
        </Header>
      }
    >
      <SpaceBetween size="l">

        {/* Overview */}
        <Container>
          <ExpandableSection headerText="ℹ️ Overview">
            <SpaceBetween size="m">
              <Box>
                Monitor and improve delivery performance across your distribution network.
                Filter by ship date range to analyse DC performance, store region delivery,
                and critical shipping lanes.
              </Box>
              <Box>
                It highlights how efficiently orders are shipped and delivered within the selected date window, 
                enabling fast detection of risks, late shipments, and operational delays.
              </Box>
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">🎯 Target</Box>
                  <Box>🟩 {target}% On-Time Adherence</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Status</Box>
                  <Box>✅ On Track &nbsp; ⚠️ Needs Attention &nbsp; 🚨 Critical</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Analysis Sections</Box>
                  <Box>Network-Wide · DC · Store Region · Critical Lanes</Box>
                </div>
              </ColumnLayout>
              <line />
              <Box>
                <h2>📘 Summary of the Performance</h2>
                Based on the dataset filtered for selected dates, the system has analyzed network-wide shipment performance and identified the following:
                <br/>
                <h3>1. Network Wide Performance</h3>
                <ul>
                  <li>Network Adherence Rate – % of orders shipped on or before the promised date</li>
                  <li>Total Orders Shipped – Count of all orders shipped across all DCs</li>
                  <li>On-Time Orders – Orders delivered on time</li>
                  <li>Late Orders – Orders delivered after the actual ship date</li>
                  <li>Unshipped Orders – Orders still pending shipment</li>
                </ul>
                <h3>2. Distribution Center Performance</h3>
                <ul>
                  <li>Highlights top-performing DCs</li>
                  <li>Flags underperforming nodes needing intervention</li>
                  <li>Shows adherence %, on-time shipments, and execution stability</li>
                </ul>
                <h3>3. Store Region Delivery Performance</h3>
                <ul>
                  <li>Identifies regions with strong on-time delivery rates</li>
                  <li>Flags regions trending toward late deliveries</li>
                  <li>Shows average delivery time and primary servicing DC</li>
                </ul>
                <h3>4. Critical Lane Analysis (DC ➝ Region)</h3>
                <ul>
                  <li>Evaluates DC ➝ Region lanes for potential delays</li>
                  <li>Surfaces systemic bottlenecks</li>
                  <li>Shows top lanes contributing to risks</li>
                </ul>
              </Box>
            </SpaceBetween>
          </ExpandableSection>
        </Container>

        {/* Date Filter */}
        <Container header={<Header variant="h2">📅 Select Ship Date Range</Header>}>
          <SpaceBetween size="m">
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div>
                <div style={dateStyle.label}>From Date</div>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={dateStyle.input} />
              </div>
              <div>
                <div style={dateStyle.label}>To Date</div>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={dateStyle.input} />
              </div>
              <Button variant="primary" onClick={handleApplyFilter} iconName="search">🔍 Filter Data</Button>
              <Button onClick={handleClearFilter}>Clear</Button>
            </div>
            {appliedRange?.from && (
              <Box color="text-body-secondary" fontSize="body-s">
                📌 Using Ship Date Range: <strong>{appliedRange.from}</strong> → <strong>{appliedRange.to}</strong>
              </Box>
            )}
          </SpaceBetween>
        </Container>

        {/* ── Section 1: Network-Wide ── */}
        <Container header={<Header variant="h2">📊 Network-Wide Performance</Header>}>
          <SpaceBetween size="m">
            <Box color="text-body-secondary">Overview of delivery adherence across all distribution centers (DCs).</Box>
            <ExpandableSection headerText="ℹ️ Additional Detail">
              <SpaceBetween size="s">
                <Box><h2>📘 Key Calculations</h2></Box>
                <Box><strong>Total Orders:</strong> All orders for the region where latest_ship_date falls within the selected date range.</Box>
                <Box><strong>On-Time</strong> = actual_ship_date ≤ latest_ship_date</Box>
                <Box><strong>Late Orders:</strong> actual_ship_date greater than latest_ship_date</Box>
                <Box><strong>Unshipped Orders:</strong> actual_ship_date is NULL</Box>
                <Box><strong>Network Adherence Rate</strong> = On-Time Orders / Total Shipped Orders × 100</Box>
                <Box><strong>This Period</strong> = selected date range &nbsp;|&nbsp; <strong>Previous Period</strong> = same-length period immediately before</Box>
              </SpaceBetween>
            </ExpandableSection>
            <Section loading={networkSection.loading} error={networkSection.error}>
              <SectionBlock
                table={networkSection.data?.table}
                aiInsight={networkSection.data?.ai_insight}
                emptyMsg="No network-wide data available for selected date range."
                title="Network-Wide Performance"
              />
            </Section>
          </SpaceBetween>
        </Container>

        {/* ── Section 2: Summary Metrics + Chart ── from kpis */}
        {/* <Container header={<Header variant="h2">Summary Metrics</Header>}>
          <Section loading={kpisSection.loading} error={kpisSection.error}>
            <ColumnLayout columns={4} variant="text-grid">
              <div><Box variant="awsui-key-label">Overall On-Time %</Box>
                <Box fontSize="display-l" fontWeight="bold"><span style={{ color: statusColor }}>{avgPct}%</span></Box></div>
              <div><Box variant="awsui-key-label">Target</Box>
                <Box fontSize="display-l" fontWeight="bold">{target}%</Box></div>
              <div><Box variant="awsui-key-label">Total Orders</Box>
                <Box fontSize="display-l" fontWeight="bold">{totalOrders.toLocaleString()}</Box></div>
              <div><Box variant="awsui-key-label">Late Orders</Box>
                <Box fontSize="display-l" fontWeight="bold"><span style={{ color: late > 0 ? "#d13212" : undefined }}>{late.toLocaleString()}</span></Box></div>
            </ColumnLayout>
          </Section>
        </Container> */}

        {/* Chart */}
        {/* {!kpisSection.loading && chartData.length > 0 && (
          <Container header={<Header variant="h2">📈 On-Time Shipment % Over Time</Header>}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={v => [`${v}%`, "On-Time %"]} />
                <ReferenceLine y={target} stroke="#e07b39" strokeDasharray="6 3"
                  label={{ value: `Target ${target}%`, position: "insideTopRight", fill: "#e07b39", fontSize: 11 }} />
                <Line type="monotone" dataKey="On-Time %" stroke="#0073bb" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Container>
        )} */}

        {/* ── Section 3: DC Performance ── */}
        <Container header={<Header variant="h2">🏭 DC Performance Summary</Header>}>
          <SpaceBetween size="m">
            <Box color="text-body-secondary">How each DC is performing against the target, regardless of destination.</Box>
            <ExpandableSection headerText="ℹ️ Additional Detail">
              <SpaceBetween size="s">
                <Box><h2>📘 Key Calculations</h2></Box>
                <Box><strong>Adherence Rate</strong> = On-Time Orders / Total Shipped Orders × 100</Box>
                <Box><strong>On-Time</strong> = actual_ship_date ≤ latest_ship_date</Box>
                <Box><strong>Late Orders by 1 Day</strong> actual_ship_date − latest_ship_date = 1</Box>
                <Box><strong>Unshipped Orders</strong> = actual_ship_date is NULL</Box>
                <Box>Sorted worst → best adherence. Filtered by selected date range.</Box>
              </SpaceBetween>
            </ExpandableSection>
            <Section loading={dcSection.loading} error={dcSection.error}>
              <SectionBlock
                table={dcSection.data?.table}
                aiInsight={dcSection.data?.ai_insight}
                emptyMsg="No DC performance data available for selected date range."
                columnHighlights={dcHighlights}
                title="DC Performance"
              />
            </Section>
          </SpaceBetween>
        </Container>

        {/* ── Section 4: Store Region ── */}
        <Container header={<Header variant="h2">🏬 Store Region Performance</Header>}>
          <SpaceBetween size="m">
            <Box color="text-body-secondary">Highlights which store groups are experiencing delays.</Box>
            <ExpandableSection headerText="ℹ️ Additional Detail">
              <SpaceBetween size="s">
                <Box><h2>📘 Key Calculations</h2></Box>
                <Box><strong>On-Time</strong> = actual_ship_date ≤ latest_ship_date</Box>
                <Box><strong>Adherence Rate</strong> = On-Time Orders / Total Orders × 100</Box>
                <Box><strong>Avg Delivery Days</strong> = latest_delivery_date − latest_ship_date (average)</Box>
                <Box>Sorted worst → best adherence. Filtered by selected date range.</Box>
              </SpaceBetween>
            </ExpandableSection>
            <Section loading={storeSection.loading} error={storeSection.error}>
              <SectionBlock
                table={storeSection.data?.table}
                aiInsight={storeSection.data?.ai_insight}
                emptyMsg="No store region performance data available for selected date range."
                title="Store Region Performance"
              />
            </Section>
          </SpaceBetween>
        </Container>

        {/* ── Section 5: Critical Lane ── */}
        <Container header={<Header variant="h2">🚨 Critical Lane Analysis (DC → Store Region)</Header>}>
          <SpaceBetween size="m">
            <Box color="text-body-secondary">Pinpoints the highest volume and most problematic shipping lanes.</Box>
            <ExpandableSection headerText="ℹ️ Additional Detail">
              <SpaceBetween size="s">
                <Box><h2>📘 Key Calculations</h2></Box>
                <Box><strong>Volume</strong> — High / Medium / Low based on relative order volume.</Box>
                <Box>
                  <strong>Volume Classification: </strong>The system dynamically determines high / medium / low lanes using the range:
                  <br/>
                  <ul>
                    <li><code>range = max(total_orders) − min(total_orders)</code></li>
                    <li><code>High    = total_orders ≥ min + 2*(range)/3</code></li>
                    <li><code>Medium  = total_orders ≥ min + (range)/3</code></li>
                    <li><code>Low     = everything below</code></li>
                  </ul>
                </Box>
                <Box><strong>Network Impact</strong> — 🚨 Highly Negative (&lt;70%) · ⚠️ Negative (&lt;85%) · ✅ Positive (≥90%)</Box>
                <Box>Sorted by volume + adherence to surface highest-risk lanes first.</Box>
              </SpaceBetween>
            </ExpandableSection>
            <Section loading={laneSection.loading} error={laneSection.error}>
              <SectionBlock
                table={laneSection.data?.table}
                aiInsight={laneSection.data?.ai_insight}
                emptyMsg="No critical lane data available for selected date range."
                title="Critical Lane Analysis"
              />
            </Section>
          </SpaceBetween>
        </Container>

        {/* KPI Detail */}
        {/* {!kpisSection.loading && kpis.length > 0 && (
          <ExpandableSection headerText={`KPI Detail Table (${kpis.length} records)`}>
            <DataTable columns={kpiColumns} data={kpis.slice(0, 200)} title="KPI Details" />
          </ExpandableSection>
        )} */}

        <Alert type="info">
          🎯 <strong>100% Real Data Analysis</strong> — All metrics and projections are based exclusively on S3 CSV data with no fabricated information.
        </Alert>

      </SpaceBetween>
    </ContentLayout>
  );
}

const dateStyle = {
  label: { fontSize: 13, fontWeight: 600, color: "#414d5c", marginBottom: 4 },
  input: { border: "1px solid #aab7b8", borderRadius: 4, padding: "6px 10px", fontSize: 14, fontFamily: "inherit", outline: "none", color: "#0d1117", cursor: "pointer" },
};